import React, { createContext, useContext, useMemo, useState } from 'react'

import { StepConfig } from '@guiker/components-library'
import { get, yup } from '@guiker/shared-framework'

export type StepWithSchema<T> = StepConfig & {
  schema?: yup.Schema<Partial<T>>
  schemaPath?: string
}

type ApiFormStepContext<T, Steps extends StepWithSchema<unknown>[] | readonly StepWithSchema<unknown>[]> = {
  data: T
  steps: Steps
  setData: React.Dispatch<React.SetStateAction<T>>
  formName?: string
  readOnly?: boolean
  stepState: {
    isSubmitting: boolean
    setIsSubmitting: (value: boolean) => void
  }
}

type ApiFormStepContextProps<
  T,
  Steps extends StepWithSchema<unknown>[] | readonly StepWithSchema<unknown>[],
> = React.PropsWithChildren & {
  defaultValue?: T
  steps: Steps
  formName?: string
  readOnly?: boolean
}

const ApiFormStepContext = createContext<ApiFormStepContext<any, any>>(null)

export const getStepWithDisabledStatus = <T,>(
  steps: StepWithSchema<unknown>[] | readonly StepWithSchema<unknown>[],
  data: T,
) => {
  let previousIsDisabled = false

  const isStepValid = (index: number) => {
    const { schema, schemaPath = '' } = { ...steps[index] }
    const schemedData = get(data, schemaPath, data)

    return schema ? !!schema.isValidSync(schemedData, { abortEarly: false, context: data }) : true
  }

  return steps.map((step, index) => {
    const currentStepIsDisabled = previousIsDisabled || !isStepValid(index - 1)

    previousIsDisabled = currentStepIsDisabled

    return {
      ...step,
      disabled: currentStepIsDisabled,
    }
  })
}

export const ApiFormStepContextProvider = <
  T,
  Steps extends StepWithSchema<unknown>[] | readonly StepWithSchema<unknown>[],
>({
  defaultValue,
  children,
  formName,
  steps: stepProps,
  readOnly,
}: ApiFormStepContextProps<T, Steps>) => {
  const [data, setData] = useState<T>(defaultValue)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const steps = useMemo(() => getStepWithDisabledStatus(stepProps, data), [data, stepProps])
  const stepState = { isSubmitting, setIsSubmitting }

  const value = { data, steps, setData, formName, readOnly, stepState }

  return <ApiFormStepContext.Provider value={value}>{children}</ApiFormStepContext.Provider>
}

export const useApiFormStepContext = <
  T,
  Steps extends StepWithSchema<unknown>[] | readonly StepWithSchema<unknown>[] = StepWithSchema<unknown>[],
>() => {
  const context = useContext<ApiFormStepContext<T, Steps>>(ApiFormStepContext)

  if (context === undefined) {
    throw new Error('useApiFormStepContext can only be used inside ApiFormStepContextProvider')
  }

  return context
}
